import { Link } from "react-router";
import {
  Gift, ChevronRight, Calendar, Ticket,
} from "lucide-react";
import { SubsidySumupSkeleton } from "@/features/subventions/components/SubsidySumupHoverCard/SubsidySumupSkeleton";
import { useSubsidySumups } from "@/features/subventions/hooks/useSubsidySumups";
import { SUBVENTIONS_PATH } from "@/features/subventions/routes";
import type { SubsidySumupGroup, SubsidySumupItem, ChequeItem } from "@/features/subventions/types/subsidy-sumup.types";
import { formatEuroWithDecimalOnlyIfNeeded } from "@/shared/utils/formatEuro";

type SumupItemProps = { item: SubsidySumupItem };

const SumupItem = ({ item }: SumupItemProps) => (
  <div className="py-3 px-1 flex items-center justify-between">
    <div>
      <span className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 flex-1">
        {item.nom}
      </span>

      {item.date_limite && (
        <div className="flex items-center gap-1 mt-0.5">
          <Calendar className="h-3 w-3 text-gray-400" />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {item.date_limite}
          </span>
        </div>
      )}
    </div>

    <span className="text-sm font-medium text-primary whitespace-nowrap">
      {item.sub}
    </span>
  </div>
);

type SumupGroupProps = { group: SubsidySumupGroup };

const SumupGroup = ({ group }: SumupGroupProps) => (
  <div className="divide-y divide-gray-100 dark:divide-gray-800">
    {group.items.map((item, index) => (
      <SumupItem key={`${group.group_name}-${index}`} item={item} />
    ))}
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-4 text-center">
    <Gift className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
    <p className="text-sm text-gray-500 dark:text-gray-400">
      Aucune subvention disponible
    </p>
  </div>
);

type ChequeItemDisplayProps = { cheque: ChequeItem };

const ChequeItemDisplay = ({ cheque }: ChequeItemDisplayProps) => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  return (
    <div className="py-3 px-1 flex items-center justify-between">
      <div>
        <span className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 flex-1">
          {cheque.motif || "Chèque cadeau"}
        </span>
        {cheque.expires_at && (
          <div className="flex items-center gap-1 mt-0.5">
            <Calendar className="h-3 w-3 text-gray-400" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              jusqu'au
              {" "}
              {formatDate(cheque.expires_at)}
            </span>
          </div>
        )}
      </div>
      <span className="text-sm font-semibold text-primary-emphasis whitespace-nowrap">
        {formatEuroWithDecimalOnlyIfNeeded(cheque.available_amount)}
      </span>
    </div>
  );
};

export const SubsidySumupHoverCard = () => {
  const { data, isLoading } = useSubsidySumups();

  const hasSubsidies = data?.subsidy_sumups && data.subsidy_sumups.length > 0;
  const cheques = data?.cheques;
  const hasCheques = cheques?.enabled && cheques.cheques.length > 0;

  return (
    <div className="w-full">
      {isLoading
        ? (
          <SubsidySumupSkeleton />
        )
        : (
          <>
            {/* Subsidies Section */}
            {hasSubsidies && (
              <>
                <div className="flex items-center gap-2 pb-3 border-b border-gray-200 dark:border-gray-700">
                  <Gift className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Mes Subventions
                  </h3>
                </div>
                <div className="space-y-4 max-h-48 overflow-y-auto pr-1">
                  {data.subsidy_sumups.map((group, index) => (
                    <SumupGroup key={`${group.group_name}-${index}`} group={group} />
                  ))}
                </div>
              </>
            )}

            {/* Empty state only if no subsidies and no cheques */}
            {!hasSubsidies && !hasCheques && <EmptyState />}
          </>
        )}

      {/* Cheques Section */}
      {!isLoading && hasCheques && cheques && (
        <div className={`rounded-xl border border-primary/15 bg-lightprimary p-3 ${hasSubsidies ? "mt-4" : ""}`}>
          <div className="flex items-center gap-2 pb-3 border-b border-primary/15">
            <Ticket className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Mes Chèques Cadeaux
            </h3>
            <span className="ml-auto text-sm font-semibold text-primary-emphasis">
              {formatEuroWithDecimalOnlyIfNeeded(cheques.total_available)}
            </span>
          </div>
          <div className="divide-y divide-primary/10 max-h-32 overflow-y-auto">
            {cheques.cheques.map((cheque) => (
              <ChequeItemDisplay key={cheque.id} cheque={cheque} />
            ))}
          </div>
        </div>
      )}

      <Link
        to={SUBVENTIONS_PATH}
        className="flex items-center justify-center gap-1 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
      >
        Voir les détails
        <ChevronRight className="h-4 w-4" />
      </Link>
    </div>
  );
};
